import {
  PDQ_FRAME_DIMENSION,
  PDQ_FRAME_TIMES,
  PDQ_JPEG_QUALITY,
} from '@/features/uploads/config/pdq';

/**
 * Client-side sample-frame extraction for the PDQ near-duplicate pre-check (gate 2).
 *
 * Grabs up to 5 frames at fixed timestamps via a hidden `<video>` + a 512×512 canvas and encodes
 * each as a JPEG blob. The frames are uploaded to the backend, which computes their PDQ hashes with
 * the SAME sidecar that hashed the stored frames — so the query/candidate hashes come from one
 * implementation and bit-exactness is never the client's problem (near-duplicate is decided within a
 * Hamming threshold, which absorbs the minor scale/encode differences between browser canvas and
 * ffmpeg). Mirrors the linewise reference sampling: times [1,5,10,15,20]s, 512×512 anamorphic, rgb.
 *
 * A browser that cannot decode the file (e.g. HEVC/ProRes with no codec) yields no frames. The upload
 * engine records the advisory as `inconclusive` without calling the backend with an illegal count;
 * authoritative post-upload intake still runs server-side.
 */

/** One extracted sample frame: its source timestamp and the encoded JPEG bytes. */
export interface ExtractedFrame {
  /** Source seek point in seconds. */
  t: number;
  /** 512×512 JPEG of the frame at `t`. */
  blob: Blob;
}

/**
 * The sample timestamps that actually fall inside a clip of `durationSecs` — the fixed offsets minus
 * any past the end (a short clip yields fewer frames; e.g. an 8 s clip → [1, 5]). Pure. A non-finite
 * or non-positive duration yields no frames.
 */
function frameTimesFor(durationSecs: number, times: readonly number[] = PDQ_FRAME_TIMES): number[] {
  if (!Number.isFinite(durationSecs) || durationSecs <= 0) return [];
  return times.filter((t) => t < durationSecs);
}

/**
 * Extract the PDQ sample frames from `file` as JPEG blobs. Resolves to `[]` (never rejects) when the
 * browser cannot decode the video or has no 2D canvas. Callers must type insufficient evidence as
 * `inconclusive`; they must not interpret an empty/partial list as no advisory match.
 */
export function extractPdqFrames(file: File): Promise<ExtractedFrame[]> {
  return new Promise((resolve) => {
    const frames: ExtractedFrame[] = [];
    let settled = false;
    let url = '';
    const done = () => {
      if (settled) return;
      settled = true;
      try {
        if (url) URL.revokeObjectURL(url);
      } catch {
        /* no object-URL support — nothing to revoke */
      }
      resolve(frames);
    };

    // Any missing browser API (no URL / no canvas / no video decode) yields no evidence. This promise
    // MUST never reject; the upload engine records inconclusive and lets authoritative intake decide.
    try {
      url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      // Muted + inline lets a detached element seek+paint without a user gesture in some browsers.
      video.playsInline = true;

      const canvas = document.createElement('canvas');
      canvas.width = PDQ_FRAME_DIMENSION;
      canvas.height = PDQ_FRAME_DIMENSION;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        done();
        return;
      }

      let times: number[] = [];
      let idx = 0;
      const seekNext = () => {
        if (idx >= times.length) {
          done();
          return;
        }
        video.currentTime = times[idx];
      };

      video.onloadedmetadata = () => {
        times = frameTimesFor(video.duration);
        seekNext();
      };
      video.onseeked = () => {
        const t = times[idx];
        try {
          ctx.drawImage(video, 0, 0, PDQ_FRAME_DIMENSION, PDQ_FRAME_DIMENSION);
          canvas.toBlob(
            (blob) => {
              if (blob) frames.push({ t, blob });
              idx += 1;
              seekNext();
            },
            'image/jpeg',
            PDQ_JPEG_QUALITY,
          );
        } catch {
          // A tainted/undecodable frame — skip it, keep whatever else we get.
          idx += 1;
          seekNext();
        }
      };
      // Undecodable codec / load failure → resolve with whatever evidence we have (usually none).
      video.onerror = done;

      video.src = url;
    } catch {
      done();
    }
  });
}
